import { Alert, Avatar, Badge, Button, Card, Dialog, Skeleton, Tooltip } from 'components/ui';
import React, { useContext, useEffect, useRef, useState } from 'react'
import { HiOutlineCheckCircle, HiOutlineDownload, HiOutlinePhotograph, HiOutlineSave, HiOutlineSaveAs, HiOutlineVideoCamera, HiSave } from 'react-icons/hi';
import getFileType, { getFileTypeImageVideo } from 'utils/fileType';
import { Draggable } from '@fullcalendar/interaction';
import HiOutlineGif from 'components/shared/HiOutlineGif';
import { UploadContext } from 'contexts/UploadContext';
import { HiOutlineChatBubbleBottomCenterText, HiOutlineTrash } from 'react-icons/hi2';
import { HiOutlineBookmark } from 'react-icons/hi2';
import { MediaContext } from 'contexts/MediaContext';
import { getAspectRatio, getCalendarDurationPerAspectRatio } from 'utils/getAspectRatio';
import { SocialIcon } from 'react-social-icons';
import { Loading, UsersAvatarGroup } from 'components/shared';
import capitalize from 'components/ui/utils/capitalize';
import { TbShare3 } from 'react-icons/tb';
import { CopyContext } from 'contexts/CopyContext';
import useDarkMode from 'utils/hooks/useDarkMode';
import acronym from 'utils/acronym';
import useTwColorByName from 'utils/hooks/useTwColorByName';
import { Trans } from 'react-i18next';
import MagicEditButton from 'components/posts_previews/components/MagicEditButton';
import ConfigPanelPostTypes from 'views/post_playground/ConfigPanelPostTypes';
import ConfirmModal from 'components/shared/ConfirmModal';
import { ModalContext } from 'contexts/ModalContext';
import { AIContext } from 'contexts/AIContext';
import { LuSave } from 'react-icons/lu';
import { PostArticleContext } from 'contexts/PostArticleContext';
import usePostGenerator from 'utils/hooks/usePostGenerator';
import FullscreenMediaViewer from 'components/shared/FullscreenMediaViewer';
import { proxyUrl } from 'utils/proxy';

const MediaElement = ({ 
  id = "",
  className = "",
  src = "", 
  originalSrc = "", 
  aspectRatio = "", 
  forceType = "", 
  hideLayout = false,
  text = "", 
  networks = [],
  mediaDescription = "",
  isStock = false,
  isDownloadable = true,
  isEditable = true,
  stockUserProfileLink,
  stockUserProfileImage,
  stockUserName,
  stockPlatform,
  stockPlatformLink,
  stockPlatformAttributionImage,
  stockDownloadEndpoint,
  selected = false,
  isDraggable = false,
  isSelectable = false,
  onError = () => void 0,
  handleOnSelect = () => void 0,
  handleOnUnselect = () => void 0,
  name = "",
  asset = undefined,
  aiImageModel = "",
  showPublishButton = true,
  user = undefined,
  onPublishCallback = undefined,
  isBulkSelectable = false,
  isBulkSelected = false,
  onToggleBulkSelect = undefined,
  isSelectionModeActive = false,
}) => {
  const bgColor = useTwColorByName();
  const mediaRef = useRef();
  const { openModal, closeModal } = useContext(ModalContext);
  const { aiModels } = useContext(AIContext);
  const { openPostArticle } = useContext(PostArticleContext);
  const { generateEmptyPost } = usePostGenerator();
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const {addFilesAsURLUploadModal, urlToFileUploadModal, presendFileToAPIUploadModal } = useContext(UploadContext);
  const {deleteAssetMyMedia} = useContext(MediaContext);
  const {deleteAssetMyCopies} = useContext(CopyContext);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDark] = useDarkMode();
  const [isLoadingAsset, setIsLoadingAsset] = useState(true);
  const [showDescription, setShowDescription] = useState(false);
  const videoRef = useRef();
  const imgRef = useRef(null);
  const isInitialMount = useRef(true);
  const hoverPlayTimeoutRef = useRef();

  const aiImageModelObj = aiModels?.find(e => e?.model == aiImageModel);
  const userObj = user || asset?.user || undefined;
  
  if (asset && !asset?.original) {
    asset.original = asset?.image?.url || asset?.video?.url;
  }
  if (asset && !asset?.src) {
    asset.src = asset?.image?.thumbnail?.url || asset?.image?.url || asset?.video?.url;
  }
  
  let type = getFileType(asset?.original ? asset?.original : name ? name : src);
  if (forceType) type = forceType;
  if (!src && text) type = "text";
  if (asset?.type == "text") type = "text";
  
  // if (asset && asset?.type) {
  //     type = asset?.type;
  // }
  
  if (asset && type != "text") {
    src = asset?.src;
    originalSrc = asset?.original;
  }
  
  if (!aspectRatio && asset) {
    if (asset?.image?.url) {
      aspectRatio = getAspectRatio(asset?.image?.width, asset?.image?.height)
    }
    if (asset?.video?.url) {
      aspectRatio = getAspectRatio(asset?.video?.width, asset?.video?.height)
    }
  }

  // While the media loads, the img/video has no intrinsic height, so the card
  // would collapse and the masonry reflows when each one pops in. Reserve the
  // final footprint with the known aspect ratio (some callers pass raw ratios
  // like "16/9" instead of a Tailwind class — fall back to a square tile).
  const placeholderAspect = typeof aspectRatio === "string" && aspectRatio.startsWith("aspect-")
    ? aspectRatio
    : "aspect-square";
  
  var isPlaying = videoRef?.current?.currentTime > 0 && !videoRef?.current?.paused && !videoRef?.current?.ended && videoRef?.current?.readyState > videoRef?.current?.HAVE_CURRENT_DATA;

  const onMediaMouseOver = () => {
    if (!videoRef?.current || isPlaying) return;
    clearTimeout(hoverPlayTimeoutRef.current);
    hoverPlayTimeoutRef.current = setTimeout(() => {
      // play() returns a promise that rejects with AbortError if pause()
      // lands before playback starts (fast hover in/out) — swallow it.
      videoRef.current?.play()?.catch(() => void 0);
    }, 160);
  };

  const onMediaMouseOut = () => {
    clearTimeout(hoverPlayTimeoutRef.current);
    videoRef?.current?.pause();
  };
  
  const setStockDownloaded = () => {
    if (isStock && stockDownloadEndpoint) {
      fetch(stockDownloadEndpoint).then((response) => response);
    }
  }
  
  const onAssetSave = () => {
    setStockDownloaded();
    addFilesAsURLUploadModal(originalSrc);
    setIsDownloading(true);
    setTimeout(() => {
      setIsDownloading(false);
    }, 5000);
  }

  // `a.download` is a DOMString — a non-string value (or one without an
  // extension) makes the browser save an unopenable, extensionless file.
  const buildDownloadFilename = (blob) => {
    const urlExt = String(originalSrc || '').split('?')[0].split('#')[0].split('.').pop();
    const mimeExt = blob?.type?.split('/')?.[1]?.split('+')?.[0];
    const ext = (/^[a-z0-9]{2,5}$/i.test(urlExt) && urlExt) || mimeExt || 'jpg';
    const date = new Date().toISOString().slice(0, 10);
    const kind = aiImageModel ? 'ai-image' : type === 'video' ? 'video' : 'image';
    return `followr-${kind}-${date}.${ext}`;
  }

  // The CDN only sends `access-control-allow-origin` when the request carries an
  // `Origin` header, so the copy the <img> tag already left in the HTTP cache has
  // no CORS headers and reusing it makes fetch() fail. `cache: 'reload'` forces a
  // fresh request; if that still fails, go through the API proxy the same way
  // useAvatars/useVoices do.
  const fetchAssetBlob = async () => {
    try {
      const response = await fetch(originalSrc, { cache: 'reload' });
      if (!response.ok) throw new Error(response.status);
      return await response.blob();
    } catch (error) {
      const response = await fetch(proxyUrl(originalSrc));
      if (!response.ok) throw new Error(response.status);
      return await response.blob();
    }
  }

  const onAssetDownload = async () => {
    try {
      setStockDownloaded();
      const blob = await fetchAssetBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildDownloadFilename(blob);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading asset:', error);
      // Network/CORS failure — at least surface the file to the user.
      if (originalSrc) window.open(originalSrc, '_blank', 'noopener');
    }
  }
  
  const handleOnClickDelete = () => {
    // setIsDeleteModalOpen(true);
    openModal(
      <ConfirmModal 
      type={"danger"}
      title={`Delete asset`}
      onCancel={closeModal}
      onConfirm={onAssetDelete}
      confirmButtonColor={"red-700"}
      >
      <p>Are you sure you want to delete this asset? This action can't be undone.</p>
      {asset?.posts?.length && asset?.posts?.filter(e => e.status != "published")?.length && 
        <Alert type="danger" className="mt-4">This asset is related to an <i>unpublished</i> post.</Alert>
      }
      </ConfirmModal>,
      500
    );
  }
  
  const onDeleteModalClose = () => {
    setIsDeleting(false);
    setIsDeleteModalOpen(false);
    closeModal();
  }
  
  const onAssetView = () => {
    setDialogIsOpen(true)
  }
  
  const onAssetViewClose = (e) => {
    setDialogIsOpen(false)
  }
  
  const onAssetDelete = () => {
    setIsDeleting(true);
    if (type == "text") {
      deleteAssetMyCopies(id || asset?.id).then((response) => {
        onDeleteModalClose();
        setIsDeleting(false);
      });
    } else {
      deleteAssetMyMedia(id || asset?.id).then((response) => {
        onDeleteModalClose();
        setIsDeleting(false);
      });
    }
  }
  
  const onAssetPublish = (network) => {
    let _url = originalSrc || src;
    let _thumbnail = src || originalSrc;
    let _asset = asset ? asset : {
      image: getFileTypeImageVideo(_url) == "image" ? {
        url: _url,
        thumbnail: {
          url: _thumbnail
        }
      } : undefined,
      video: getFileTypeImageVideo(_url) == "video" ? {
        url: _url
      } : undefined,
    };
    
    let emptyPostGroup = generateEmptyPost(network);
    emptyPostGroup.posts[0].assets = [_asset];
    openPostArticle(emptyPostGroup);
    onPublishCallback && onPublishCallback();
  }
  
  const onAssetSelect = () => {
    setStockDownloaded();
    handleOnSelect(originalSrc, asset);
  }
  
  const onAssetUnselect = () => {
    handleOnUnselect(originalSrc, asset);
  }
  
  const isHTML = (str) => {
    var doc = new DOMParser().parseFromString(str, "text/html");
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
  }
  
  useEffect(() => {
    if (mediaRef.current && isDraggable) {
        isInitialMount.current = false;

        let currAssets;

        if (isStock) {
            const _url = originalSrc || src;
            const _thumbnail = src || originalSrc;

            currAssets = {
                image: getFileTypeImageVideo(_url) === "image" ? {
                    url: _url,
                    thumbnail: {
                        url: _thumbnail
                    }
                } : undefined,
                video: getFileTypeImageVideo(_url) === "video" ? {
                    url: _url
                } : undefined,
            };
        } else {
            const { id, original } = asset || {};
            currAssets = id ? asset : {
                id,
                image: getFileTypeImageVideo(original) === "image" ? {
                    url: original,
                    thumbnail: {
                        url: asset?.src
                    }
                } : undefined,
                video: getFileTypeImageVideo(original) === "video" ? {
                    url: original
                } : undefined,
            };
        }

        if (currAssets) {
            new Draggable(mediaRef.current, {
                eventData: {
                    title: 'New event',
                    duration: getCalendarDurationPerAspectRatio(aspectRatio),
                    post_group: {
                        posts: [{
                            preferences: {
                                media_product_type: "FEED"
                            },
                            assets: [currAssets]
                        }]
                    }
                }
            });
        }
    }
  }, [mediaRef, src, isDraggable, isStock, asset, originalSrc]);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setIsLoadingAsset(false);
    }
    if (videoRef.current?.readyState >= 1) {
      setIsLoadingAsset(false);
    }
  }, [src]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && dialogIsOpen) {
        onAssetViewClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [dialogIsOpen, onAssetViewClose]);
  
  if (!src && !text && !asset?.type) return <></>;
  if (!src && !originalSrc && (asset?.type == "image" || asset?.type == "video" || asset?.type == "gif")) return <></>;
  if (type == "text") return <></>;
  
  return (<>
    <Card
      key={asset?.id}
      ref={mediaRef}
      onMouseOver={onMediaMouseOver}
      onMouseOut={onMediaMouseOut}
      className={`group relative transition-all cursor-zoom-in ${isDraggable && `!cursor-pointer hover:drop-shadow-[0_10px_8px_rgba(0,0,0,0.2)] hover:rotate-[2deg]`}  ${selected && `border-[5px] rounded-2xl border-primary-600 shadow`} ${isBulkSelected ? `ring-3 ring-blue-500 ring-offset-2 ${isDark ? 'ring-offset-gray-900' : 'ring-offset-white'}` : ''} ${className}`}
      onClick={() => {
        if (isSelectionModeActive && onToggleBulkSelect) {
          onToggleBulkSelect(asset?.id || id);
        } else {
          onAssetView();
        }
      }}
      bodyClass={`!p-0`}
    >
    {/* Bulk selection checkbox */}
    {isBulkSelectable && !isLoadingAsset && (
      <div
        className={`absolute top-2.5 left-2.5 z-[20] transition-all duration-200 ${
          isSelectionModeActive || isBulkSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
        onClick={(e) => { e.stopPropagation(); onToggleBulkSelect && onToggleBulkSelect(asset?.id || id); }}
      >
        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all
          ${isBulkSelected
            ? 'bg-blue-500 border-blue-500 text-white shadow-lg scale-110'
            : 'bg-black/30 border-white/80 backdrop-blur-sm hover:bg-black/50 hover:scale-110'
          }
        `}>
          {isBulkSelected && (
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </div>
    )}
    {isLoadingAsset &&
      <div className="absolute inset-0 z-[1] rounded-lg overflow-hidden">
        <Skeleton
        variant="block"
        className="w-full h-full absolute left-0 top-0 rounded-lg"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          {type == "video"
            ? <HiOutlineVideoCamera className="text-2xl text-gray-400 dark:text-gray-500 opacity-60" />
            : <HiOutlinePhotograph className="text-2xl text-gray-400 dark:text-gray-500 opacity-60" />
          }
        </div>
      </div>
    }
    {type == "image" && (
      <img ref={imgRef} src={src} width={asset?.image?.width} height={asset?.image?.height} onError={onError} onLoad={() => setIsLoadingAsset(false)} className={`w-full rounded-lg ${isLoadingAsset ? `${placeholderAspect} object-cover opacity-0` : ``} ${!showDescription && `group-hover:opacity-[0.85]`} transition-opacity duration-300`} />
    )}
    {type == "gif" && (
      <img ref={imgRef} src={originalSrc} width={asset?.image?.width} height={asset?.image?.height} onError={onError} onLoad={() => setIsLoadingAsset(false)} className={`w-full rounded-lg ${isLoadingAsset ? `${placeholderAspect} object-cover opacity-0` : ``} ${!showDescription && `group-hover:opacity-[0.85]`} transition-opacity duration-300`} />
    )}
    {type == "video" && (
      <video
        ref={videoRef}
        muted={true}
        src={src}
        width={asset?.video?.width}
        height={asset?.video?.height}
        preload="metadata"
        autoPlay={false}
        controls={false}
        onLoadedMetadata={() => setIsLoadingAsset(false)}
        className={`w-full rounded-lg ${isLoadingAsset ? `${placeholderAspect} object-cover opacity-0` : ``} ${!showDescription && `group-hover:opacity-[0.85]`} transition-opacity duration-300`}
      />
    )}
    
    {type == "image" && !hideLayout && !isLoadingAsset && (
      <div className="absolute top-3 right-4 drop-shadow z-[11]">
        <Tooltip title={<Trans i18nKey={'mediaElement.imageLabel'} />} placement="bottom-end">
          <HiOutlinePhotograph className="text-white text-xl" />
        </Tooltip>
      </div>
    )}
    {type == "gif" && !hideLayout && !isLoadingAsset && (
      <div className="absolute top-3 right-4 drop-shadow z-[11] rounded-lg">
        <Tooltip title={<Trans i18nKey={'mediaElement.gifLabel'} />} placement="bottom-end">
          <HiOutlineGif className="text-white text-xl" />
        </Tooltip>
      </div>
    )}
    {type == "video" && !hideLayout && !isLoadingAsset && (
      <div className="absolute top-3 right-4 drop-shadow z-[11]">
        <Tooltip title={<Trans i18nKey={'mediaElement.videoLabel'} />} placement="bottom-end">
          <HiOutlineVideoCamera className="text-white text-xl" />
        </Tooltip>
      </div>
    )}

    {aiImageModelObj && !hideLayout && !isLoadingAsset && !aiImageModelObj?.isAvatarModel && !aiImageModelObj?.isViralShortModel && (
      <div className="absolute top-3.5 right-12 drop-shadow z-[13] !cursor-default">
        <Tooltip title={aiImageModelObj?.fullName} placement="bottom-end">
          <img src={aiImageModelObj?.image} className={`w-4 h-4 ${aiImageModelObj?.driver == `ideogram` || aiImageModelObj?.driver == `openai` ? `invert` : ``}`} />
        </Tooltip>
      </div>
    )}
    
    {asset?.posts && asset?.posts?.length > 0 && !hideLayout && !isLoadingAsset && (
      <Badge className="absolute top-3 left-4 z-[11] bg-red-50 text-red-600 font-semibold" content={<Trans i18nKey={'mediaElement.usedLabel'} />} />
    )}

    {!hideLayout && !isLoadingAsset && <div className="absolute left-0 top-0 w-full h-[60%] z-10 flex flex-col justify-between p-4 pb-2 bg-gradient-to-t from-transparent via-transparent to-gray-900/60 rounded-lg"></div>}

    {!hideLayout && !isLoadingAsset && <div className="absolute left-0 top-0 w-full h-full z-[12] flex flex-col justify-between p-4 pb-2 bg-gradient-to-b from-transparent via-transparent to-gray-900/90 rounded-lg">
      {isStock && stockPlatformLink && (
        <div className={`opacity-0 pointer-events-none group-hover:opacity-[1] group-hover:pointer-events-normal flex gap-1.5 !text-[10px] absolute top-3 left-4 !text-white mt-0.5`}>
        {!stockPlatformAttributionImage ? (
          <>
          <Avatar
            shape="circle"
            size={16}
            src={stockUserProfileImage || ""}
            className={`${bgColor(stockUserName)} !text-[10px]`}
          >
            {acronym(stockUserName)}
          </Avatar>
          <span>
          {<Trans i18nKey={'mediaElement.byLabel'} />}{' '}
          <a href={stockUserProfileLink || `#`} className="underline" target="_blank">
          {stockUserName}
          </a>{' '}
          {<Trans i18nKey={'mediaElement.onLabel'} />}{' '}
          <a href={stockPlatformLink} className="underline" target="_blank">
          {stockPlatform}
          </a>
          </span>
          </>
        ) : (
          <>
          <a href={stockPlatformLink} className="underline" target="_blank">
          <img src={stockPlatformAttributionImage} className="h-[12px] w-auto mt-0.5" />
          </a>
          </>
        )}
        </div>
      )}
      
      {asset?.id ? (
        <div className="mb-4 flex items-center gap-1 h-5">
        {[...new Set([asset?.social_network_type, ...(asset?.children?.length ? asset?.children?.map(item => item?.social_network_type) : [])])]?.filter(e => e != null)?.map((network) => (
          <Tooltip title={capitalize(network)}>
          <SocialIcon network={network} className="!w-5 !h-5" fgColor={isDark ? `#F6F6F6` : undefined} />
          </Tooltip>
        ))}
        </div>
      ) : (
        <div></div>
      )}
      <div className="flex items-center justify-between mt-2 z-[12] relative">
        {!!userObj ? (
          <UsersAvatarGroup
            avatarProps={{ size: 25 }}
            className="hidden lg:inline-block"
            users={[userObj, ...new Set((asset?.children || [])?.map(e => e.user))].filter((value, index, self) => index === self.findIndex((t) => (t?.id === value?.id))) || []}
          />
        ) : (
          <div></div>
        )}
        <div className="flex items-center">
          {!!mediaDescription && 
            <Tooltip placement='top' title={`Description`}>
              <Button
                shape="round"
                variant="plain"
                size="sm"
                className="!text-white !bg-transparent"
                onClick={(e) => { e.stopPropagation(); setShowDescription(true); }}
                icon={<HiOutlineChatBubbleBottomCenterText />}
              />
            </Tooltip>
          }
          {!!asset?.id && !isSelectable && (
            <Tooltip title={<Trans i18nKey={'mediaElement.deleteLabel'} />}>
            <Button
            shape="round"
            variant="plain"
            size="sm"
            className="!text-white !bg-transparent"
            onClick={(e) => { e.stopPropagation(); handleOnClickDelete(); }}
            icon={<HiOutlineTrash />}
            />
            </Tooltip>
          )}
          {!isSelectable && isDownloadable && !asset?.id && (
            <>
              <Tooltip title={<Trans i18nKey={'mediaElement.saveLabel'} />}>
                <Button
                  shape="circle"
                  variant="plain"
                  size="sm"
                  className="!text-white !bg-transparent"
                  onClick={(e) => { e.stopPropagation(); onAssetSave(); }}
                  disabled={isDownloading}
                  icon={!isDownloading ? <LuSave /> : <Loading type={'element'} size={16} />}
                />
              </Tooltip>
            </>
          )}
          <Tooltip title={<Trans i18nKey={'mediaElement.downloadLabel'} />}>
            <Button
              shape="circle"
              variant="plain"
              size="sm"
              className="!text-white !bg-transparent"
              onClick={(e) => { e.stopPropagation(); onAssetDownload(); }}
              icon={<HiOutlineDownload />}
            />
          </Tooltip>
          {isEditable && !isSelectable && !isStock && 
            <Tooltip placement='bottom' title={<Trans i18nKey={`mediaElement.editLabel`} />}>
            <MagicEditButton 
              size="xs" 
              asset={asset || {
                image: {
                  url: originalSrc,
                  thumbnail: {
                    url: src
                  }
                }
              }} 
            />
            </Tooltip>
          }
          {!isSelectable && showPublishButton && (
            <span onClick={(e) => e.stopPropagation()}>
            <ConfigPanelPostTypes
            postType={undefined}
            setPostType={(postType) => onAssetPublish(postType)}
            menuClassName="!max-h-[160px] !z-[150] zoom-90 max-w-[200px]"
            placement="top-end"
            hideChecks={true}
            renderTitle={
              <Tooltip title={<Trans i18nKey={'mediaElement.publishLabel'} />} wrapperClass="ml-1.5">
              <Button
              variant="solid"
              size="xs"
              shape="round"
              className="!rounded-xl"
              icon={<TbShare3 />}
              />
              </Tooltip>
            }
            />
            </span>
          )}
          {isSelectable && (
            <Tooltip title={<Trans i18nKey={'mediaElement.selectLabel'} />} wrapperClass="ml-1.5">
            {!selected 
              ? <Button
              shape="circle"
              variant=""
              size="xs"
              onClick={(e) => { e.stopPropagation(); onAssetSelect(); }}
              icon={<HiOutlineCheckCircle />}
              >
              {<Trans i18nKey={'mediaElement.selectLabel'} />}
              </Button>
              : <Button
              shape="circle"
              variant="solid"
              size="xs"
              onClick={(e) => { e.stopPropagation(); onAssetUnselect(); }}
              icon={<HiOutlineCheckCircle />}
              >
              {<Trans i18nKey={'mediaElement.selectedLabel'} />}
              </Button>
            }
            
            </Tooltip>
          )}
        </div>
      </div>
    </div>}
      
      <div onClick={(e) => e.stopPropagation()} className={`absolute pointer-events-none cursor-default cursor-normal opacity-0 rounded-lg z-[100] text-white ${showDescription && `!pointer-events-auto opacity-100`} left-0 top-0 w-full h-full flex justify-center items-center bg-black/90`}>
      <div className='flex justify-end items-center p-2 px-4 absolute top-0 left-0 w-full'>
      <span 
      className='text-2xl cursor-pointer' 
      onClick={(e) => { e.stopPropagation(); setShowDescription(false); }}
      >
      &times;
      </span>
      </div>
      <p className='text-center text-sm p-4 cursor-text overflow-y-auto max-h-full'>
      {mediaDescription}
      </p>
      </div>
      </Card>      
      
      <FullscreenMediaViewer
        open={dialogIsOpen}
        src={originalSrc}
        type={type}
        onClose={onAssetViewClose}
      />
      </>);
    }
    
    export default MediaElement
    

